// An expired question where the session's transcript shows it (expired.ts):
// who asked, and whether it was you, what it asks in one line, "Urgent" and
// when it was revised when they hold, and that its agent stopped waiting
// and what it went on with. "Answer anyway" opens the one question card
// (QuestionCard.tsx) in its place, at the version it shows; opening it sends
// nothing. A revision shows in the same place; so does an answer, until the
// board no longer holds the question.
import { useEffect, useRef, useState } from 'react'
import type { Question } from '../api/types'
import { QuestionIcon } from '../code/icons'
import { useBoardState } from '../store/context'
import { askTitle, putTo } from './asks'
import { expiredItemId } from './expired'
import { QuestionCard } from './QuestionCard'
import { expiredText, QuestionFlags, QuestionRevised } from './waiting'

export function ExpiredQuestion({ question, asker }: { question: Question; asker: string }) {
  const { me, issues } = useBoardState()
  const [open, setOpen] = useState(false)
  const [focus, setFocus] = useState(false)
  const root = useRef<HTMLElement>(null)
  const now = Date.now()
  const asked = `${asker} asked${putTo(question, me?.member?.id) ? ' you' : ''}`
  const answering = open || question.state !== 'open'

  // Into the card he opened, or back to the button that opens it.
  useEffect(() => {
    if (!focus) return
    root.current?.querySelector<HTMLElement>(open ? '.question-card :is(input, textarea)' : '.expired-answer')?.focus()
    setFocus(false)
  }, [focus, open])

  const toggle = (to: boolean) => {
    setOpen(to)
    setFocus(true)
  }

  return (
    <article className="code-item code-asked is-expired" id={expiredItemId(question.id)} tabIndex={-1} aria-label={`${asked}, expired`} ref={root}>
      <header className="code-asked-head">
        <QuestionIcon size={13} />
        <strong>{asked}</strong>
        {/* Open, the card says them. */}
        {!answering && (
          <>
            {' '}
            <QuestionFlags question={question} now={now} />
            <QuestionRevised question={question} now={now} first />
          </>
        )}
      </header>
      {answering ? (
        <QuestionCard
          question={question}
          issue={question.issue ? issues.get(question.issue) : undefined}
          flags={false}
          foot={
            question.state === 'open' && (
              <button type="button" className="button button-quiet" onClick={() => toggle(false)}>
                Hide
              </button>
            )
          }
        />
      ) : (
        <>
          <p className="code-asked-title">{askTitle(question)}</p>
          {question.expiry && (
            <p className="request-note question-expired">{expiredText(question.expiry, now)}</p>
          )}
          <div className="form-actions">
            <button type="button" className="button button-primary expired-answer" aria-expanded={false} onClick={() => toggle(true)}>
              Answer anyway
            </button>
          </div>
        </>
      )}
    </article>
  )
}
